/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';

import { ApiResponseConversationMessages } from '../models/api-response-conversation-messages';
import { ApiResponseListCreateVariableMessage } from '../models/api-response-list-create-variable-message';
import { ApiResponseListMessage } from '../models/api-response-list-message';
import { ApiResponseMessage } from '../models/api-response-message';
import { createMessage } from '../fn/message-api/create-message';
import { CreateMessage$Params } from '../fn/message-api/create-message';
import { createMultiProductMessage } from '../fn/message-api/create-multi-product-message';
import { CreateMultiProductMessage$Params } from '../fn/message-api/create-multi-product-message';
import { downloadAttachment } from '../fn/message-api/download-attachment';
import { DownloadAttachment$Params } from '../fn/message-api/download-attachment';
import { forwardMessage } from '../fn/message-api/forward-message';
import { ForwardMessage$Params } from '../fn/message-api/forward-message';
import { generateAudio } from '../fn/message-api/generate-audio';
import { GenerateAudio$Params } from '../fn/message-api/generate-audio';
import { getVariablesContextValues } from '../fn/message-api/get-variables-context-values';
import { GetVariablesContextValues$Params } from '../fn/message-api/get-variables-context-values';
import { listMessages } from '../fn/message-api/list-messages';
import { ListMessages$Params } from '../fn/message-api/list-messages';
import { listMessagesOfContacts } from '../fn/message-api/list-messages-of-contacts';
import { ListMessagesOfContacts$Params } from '../fn/message-api/list-messages-of-contacts';
import { listMessagesPaginated } from '../fn/message-api/list-messages-paginated';
import { ListMessagesPaginated$Params } from '../fn/message-api/list-messages-paginated';

@Injectable({ providedIn: 'root' })
export class MessageApiService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `createMessage()` */
  static readonly CreateMessagePath = '/v1/api/ui/message';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createMessage()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  createMessage$Response(params: CreateMessage$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseMessage>> {
    return createMessage(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createMessage$Response()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  createMessage(params: CreateMessage$Params, context?: HttpContext): Observable<ApiResponseMessage> {
    return this.createMessage$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseMessage>): ApiResponseMessage => r.body)
    );
  }

  /** Path part for operation `listMessagesPaginated()` */
  static readonly ListMessagesPaginatedPath = '/v1/api/ui/message/search/{convReference}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listMessagesPaginated()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  listMessagesPaginated$Response(params: ListMessagesPaginated$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseConversationMessages>> {
    return listMessagesPaginated(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listMessagesPaginated$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  listMessagesPaginated(params: ListMessagesPaginated$Params, context?: HttpContext): Observable<ApiResponseConversationMessages> {
    return this.listMessagesPaginated$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseConversationMessages>): ApiResponseConversationMessages => r.body)
    );
  }

  /** Path part for operation `createMultiProductMessage()` */
  static readonly CreateMultiProductMessagePath = '/v1/api/ui/message/multi-product';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createMultiProductMessage()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createMultiProductMessage$Response(params: CreateMultiProductMessage$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseMessage>> {
    return createMultiProductMessage(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createMultiProductMessage$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createMultiProductMessage(params: CreateMultiProductMessage$Params, context?: HttpContext): Observable<ApiResponseMessage> {
    return this.createMultiProductMessage$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseMessage>): ApiResponseMessage => r.body)
    );
  }

  /** Path part for operation `forwardMessage()` */
  static readonly ForwardMessagePath = '/v1/api/ui/message/forward';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `forwardMessage()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  forwardMessage$Response(params: ForwardMessage$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseListMessage>> {
    return forwardMessage(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `forwardMessage$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  forwardMessage(params: ForwardMessage$Params, context?: HttpContext): Observable<ApiResponseListMessage> {
    return this.forwardMessage$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseListMessage>): ApiResponseListMessage => r.body)
    );
  }

  /** Path part for operation `listMessagesOfContacts()` */
  static readonly ListMessagesOfContactsPath = '/v1/api/ui/message/contact/{contactRef}/list';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listMessagesOfContacts()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  listMessagesOfContacts$Response(params: ListMessagesOfContacts$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseConversationMessages>> {
    return listMessagesOfContacts(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listMessagesOfContacts$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  listMessagesOfContacts(params: ListMessagesOfContacts$Params, context?: HttpContext): Observable<ApiResponseConversationMessages> {
    return this.listMessagesOfContacts$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseConversationMessages>): ApiResponseConversationMessages => r.body)
    );
  }

  /** Path part for operation `listMessages()` */
  static readonly ListMessagesPath = '/v1/api/ui/message/{reference}/list';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listMessages()` instead.
   *
   * This method doesn't expect any request body.
   */
  listMessages$Response(params: ListMessages$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseConversationMessages>> {
    return listMessages(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listMessages$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listMessages(params: ListMessages$Params, context?: HttpContext): Observable<ApiResponseConversationMessages> {
    return this.listMessages$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseConversationMessages>): ApiResponseConversationMessages => r.body)
    );
  }

  /** Path part for operation `downloadAttachment()` */
  static readonly DownloadAttachmentPath = '/v1/api/ui/message/media/{messageReference}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `downloadAttachment()` instead.
   *
   * This method doesn't expect any request body.
   */
  downloadAttachment$Response(params: DownloadAttachment$Params, context?: HttpContext): Observable<StrictHttpResponse<Blob>> {
    return downloadAttachment(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `downloadAttachment$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  downloadAttachment(params: DownloadAttachment$Params, context?: HttpContext): Observable<Blob> {
    return this.downloadAttachment$Response(params, context).pipe(
      map((r: StrictHttpResponse<Blob>): Blob => r.body)
    );
  }

  /** Path part for operation `generateAudio()` */
  static readonly GenerateAudioPath = '/v1/api/ui/message/generate-audio';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `generateAudio()` instead.
   *
   * This method doesn't expect any request body.
   */
  generateAudio$Response(params: GenerateAudio$Params, context?: HttpContext): Observable<StrictHttpResponse<Blob>> {
    return generateAudio(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `generateAudio$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  generateAudio(params: GenerateAudio$Params, context?: HttpContext): Observable<Blob> {
    return this.generateAudio$Response(params, context).pipe(
      map((r: StrictHttpResponse<Blob>): Blob => r.body)
    );
  }

  /** Path part for operation `getVariablesContextValues()` */
  static readonly GetVariablesContextValuesPath = '/v1/api/ui/message/context-variables';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getVariablesContextValues()` instead.
   *
   * This method doesn't expect any request body.
   */
  getVariablesContextValues$Response(params: GetVariablesContextValues$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseListCreateVariableMessage>> {
    return getVariablesContextValues(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getVariablesContextValues$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getVariablesContextValues(params: GetVariablesContextValues$Params, context?: HttpContext): Observable<ApiResponseListCreateVariableMessage> {
    return this.getVariablesContextValues$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseListCreateVariableMessage>): ApiResponseListCreateVariableMessage => r.body)
    );
  }

}
